import React from "react";
import {
    Phone,
    Mail,
    Globe,
    Printer,
    PawPrint,
    Users,
    CalendarCheck,
    HeartHandshake,
    ShieldCheck,
    Home as HomeIcon,
    Check,
    Sparkles,
} from "lucide-react";

const LOGO = "/photos/logo.png";
const PHOTO_PORCH = "/photos/home-10.jpg";
const PHOTO_DINING = "/photos/flyer-2.jpg";
const PHOTO_LIVING = "/photos/flyer-3.jpg";
const PHOTO_BEDROOM = "/photos/home-13.jpg";

const REQS = [
    { icon: Users, text: "Peer-run environment" },
    { icon: ShieldCheck, text: "100% clean & sober · random drug testing" },
    { icon: CalendarCheck, text: "2–3 recovery meetings per week" },
    { icon: HeartHandshake, text: "Weekly house meetings" },
    { icon: HomeIcon, text: "Keep the home clean & respect everyone" },
    { icon: PawPrint, text: "A pet-free home" },
];

const FlyerPage = () => {
    const handlePrint = () => window.print();

    return (
        <div className="flyer-screen">
            {/* Screen-only toolbar */}
            <div className="no-print sticky top-0 z-50 bg-[#FAF9F6] border-b border-[#DDD5C8]">
                <div className="max-w-[8.5in] mx-auto px-6 py-3 flex items-center justify-between">
                    <a
                        href="/"
                        className="text-sm text-[#4A5D4E] hover:text-[#2C352D]"
                    >
                        ← Back to site
                    </a>
                    <div className="text-xs tracking-[0.2em] uppercase text-[#7B8A7E]">
                        Printable Flyer · 8.5 × 11 in
                    </div>
                    <button
                        onClick={handlePrint}
                        data-testid="flyer-print-button"
                        className="btn-brand inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium"
                    >
                        <Printer className="w-4 h-4" />
                        Print / Save PDF
                    </button>
                </div>
            </div>

            <div className="flyer-wrap">
                <div className="flyer-page">
                    {/* Decorative top band */}
                    <div className="flyer-band" />

                    {/* Header */}
                    <header className="flyer-header">
                        <img
                            src={LOGO}
                            alt="Hope Recovery Home logo"
                            className="flyer-logo"
                        />
                        <p className="flyer-tagline">
                            A Place to Heal. A Path to Grow.
                            <br />
                            A Future to Flourish.
                        </p>
                    </header>

                    {/* Headline */}
                    <section className="flyer-headline">
                        <div className="flyer-eyebrow">
                            <Sparkles className="w-3 h-3" />
                            Now Open · Welcoming Inquiries
                        </div>
                        <h1 className="flyer-h1">
                            A new dawn. <span className="italic">A fresh start.</span>
                        </h1>
                        <p className="flyer-lede">
                            A peer-run transitional living home for men committed
                            to rebuilding their lives after treatment.
                        </p>
                    </section>

                    {/* Photo strip */}
                    <section className="flyer-photos">
                        <img src={PHOTO_PORCH} alt="Front porch" />
                        <img src={PHOTO_DINING} alt="Dining area" />
                        <img src={PHOTO_LIVING} alt="Living room" />
                        <img src={PHOTO_BEDROOM} alt="Shared bedroom with two beds" />
                    </section>

                    {/* Two-column body */}
                    <section className="flyer-grid">
                        {/* Left: requirements */}
                        <div>
                            <div className="flyer-section-label">
                                What we look for
                            </div>
                            <ul className="flyer-reqs">
                                {REQS.map(({ icon: Icon, text }, i) => (
                                    <li key={i}>
                                        <span className="flyer-req-icon">
                                            <Icon className="w-3.5 h-3.5" />
                                        </span>
                                        <span>{text}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>

                        {/* Right: pricing + contact */}
                        <div className="flyer-side">
                            <div className="flyer-price">
                                <div className="flyer-section-label flyer-section-label-light">
                                    Pricing
                                </div>
                                <div className="flyer-price-row">
                                    <span className="flyer-price-big">$500</span>
                                    <span className="flyer-price-unit">/ month rent</span>
                                </div>
                                <div className="flyer-price-sub">
                                    + <strong>$100</strong> deposit
                                </div>
                                <p className="flyer-price-note">
                                    First month's rent prorated for mid-month move-ins.
                                </p>
                            </div>

                            <div className="flyer-section-label mt-4">
                                Get in touch
                            </div>
                            <ul className="flyer-contact">
                                <li>
                                    <Phone className="w-3.5 h-3.5" />
                                    <span>
                                        <strong>Kim</strong> — (928) 450-6132
                                    </span>
                                </li>
                                <li>
                                    <Phone className="w-3.5 h-3.5" />
                                    <span>
                                        <strong>Arturo</strong> — (928) 303-6136
                                    </span>
                                </li>
                                <li>
                                    <Mail className="w-3.5 h-3.5" />
                                    <span className="break-all">
                                        transitionalliving@<wbr />hoperecoveryhomellc.com
                                    </span>
                                </li>
                                <li>
                                    <Globe className="w-3.5 h-3.5" />
                                    <span>hoperecoveryhomellc.com</span>
                                </li>
                            </ul>
                        </div>
                    </section>

                    {/* Quote */}
                    <section className="flyer-quote">
                        <p>
                            "A dream God placed on our hearts — a safe,
                            supportive place for men ready for a new beginning."
                        </p>
                        <span>— Kim &amp; Arturo Montana</span>
                    </section>

                    {/* Footer */}
                    <footer className="flyer-footer">
                        <span>© {new Date().getFullYear()} Hope Recovery Home LLC · Arizona</span>
                        <span className="font-semibold">hoperecoveryhomellc.com</span>
                    </footer>
                </div>
            </div>
        </div>
    );
};

export default FlyerPage;
